(() => {
  "use strict";

  if (document.documentElement.dataset.looomStoreLocatorReady === "true")
    return;
  document.documentElement.dataset.looomStoreLocatorReady = "true";

  const LEAFLET_SCRIPT_SELECTOR = 'script[data-looom-leaflet="true"]';
  let leafletPromise;

  const loadLeaflet = (source) => {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;

    leafletPromise = new Promise((resolve, reject) => {
      if (!source) {
        reject(new Error("The local Leaflet asset URL is missing."));
        return;
      }

      const existingScript = document.querySelector(LEAFLET_SCRIPT_SELECTOR);
      const script = existingScript || document.createElement("script");

      const handleLoad = () => {
        if (window.L) {
          resolve(window.L);
        } else {
          reject(new Error("Leaflet loaded without exposing its API."));
        }
      };

      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener(
        "error",
        () => reject(new Error("Leaflet could not be loaded.")),
        {
          once: true,
        },
      );

      if (!existingScript) {
        script.src = source;
        script.dataset.looomLeaflet = "true";
        document.head.appendChild(script);
      }
    });

    return leafletPromise;
  };

  class LooomStoreLocator {
    constructor(element) {
      this.element = element;
      this.resultsElement = element.querySelector(
        "[data-looom-store-locator-results]",
      );
      this.mapElement = element.querySelector("[data-looom-store-locator-map]");
      this.statusElement = element.querySelector(
        "[data-looom-store-locator-status]",
      );
      this.searchForm = element.querySelector(
        "[data-looom-store-locator-search]",
      );
      this.searchInput = element.querySelector(
        "[data-looom-store-locator-search-input]",
      );
      this.searchButton = element.querySelector(
        "[data-looom-store-locator-search-button]",
      );
      this.searchClearButton = element.querySelector(
        "[data-looom-store-locator-search-clear]",
      );
      this.dataElement = element.querySelector(
        "[data-looom-store-locator-data]",
      );
      this.translationsElement = element.querySelector(
        "[data-looom-store-locator-translations]",
      );
      this.translations = this.translationsElement
        ? JSON.parse(this.translationsElement.textContent)
        : {};
      this.filterInputs = [
        ...element.querySelectorAll(".looom-store-locator__radio"),
      ];
      this.markers = new Map();
      this.stores = [];
      this.filteredStores = [];
      this.activeStoreId = null;
      this.userPosition = null;
      this.searchPosition = null;
      this.searchQuery = "";
      this.searchMarker = null;
      this.geocodeController = null;
      this.map = null;
      this.destroyed = false;
      this.onResize = this.debounce(() => this.map?.invalidateSize(), 120);
    }

    async init() {
      try {
        this.stores = this.readStores();
        this.updateAvailableFilters();
        this.bindEvents();
        const L = await loadLeaflet(this.element.dataset.leafletUrl);
        if (this.destroyed) return;
        this.L = L;
        this.initializeMap();
        this.applyFilters();
        this.updateClearButton();
        this.requestUserLocation();
      } catch (error) {
        this.showMessage(this.translate("mapLoadError"));
        this.announce(this.translate("mapLoadErrorAnnounce"));
        console.error("[LOOOM store locator]", error);
      }
    }

    readStores() {
      let data = [];

      try {
        data = JSON.parse(this.dataElement?.textContent || "[]");
      } catch (error) {
        console.error("[LOOOM store locator] Invalid store data.", error);
      }

      return data
        .map((store, index) => ({
          id: String(store.id || `store-${index}`),
          name: this.toText(store.name),
          address: this.toText(store.address),
          postalCode: this.toText(store.postalCode),
          city: this.toText(store.city),
          country: this.toText(store.country),
          latitude: Number.parseFloat(store.latitude),
          longitude: Number.parseFloat(store.longitude),
          storeType: this.normalizeType(store.storeType),
          phone: this.toText(store.phone),
          website: this.toText(store.website),
          distance: null,
        }))
        .filter(
          (store) =>
            store.name &&
            Number.isFinite(store.latitude) &&
            Number.isFinite(store.longitude) &&
            Math.abs(store.latitude) <= 90 &&
            Math.abs(store.longitude) <= 180,
        );
    }

    toText(value) {
      if (Array.isArray(value)) return value.join(", ").trim();
      if (value && typeof value === "object") {
        return String(value.url || value.value || "").trim();
      }
      return String(value ?? "").trim();
    }

    normalizeType(value) {
      const type = this.normalizeText(this.toText(value));
      if (type.includes("horeca") || type.includes("hospitality"))
        return "horeca";
      if (type.includes("supermarkt") || type.includes("supermarket"))
        return "supermarket";
      if (
        type.includes("winkel") ||
        type.includes("retail") ||
        type.includes("shop")
      )
        return "retail";
      return type;
    }

    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase()
        .trim();
    }

    updateAvailableFilters() {
      const allInput = this.filterInputs.find((input) => input.value === "all");

      this.filterInputs.forEach((input) => {
        if (input.value === "all") return;

        const isAvailable = this.stores.some(
          (store) => store.storeType === input.value,
        );
        const filter = input.closest(".looom-store-locator__filter");

        input.disabled = !isAvailable;
        if (filter) filter.hidden = !isAvailable;

        if (!isAvailable && input.checked && allInput) {
          allInput.checked = true;
        }
      });
    }

    bindEvents() {
      this.filterInputs.forEach((input) => {
        input.addEventListener("change", () => this.applyFilters());
      });
      this.searchInput?.addEventListener("input", () =>
        this.updateClearButton(),
      );
      this.searchInput?.addEventListener("keydown", (event) => {
        if (
          event.key !== "Escape" ||
          (!this.searchInput.value && !this.searchQuery)
        )
          return;
        event.preventDefault();
        this.clearSearch();
      });
      this.searchClearButton?.addEventListener("click", () =>
        this.clearSearch(),
      );
      this.searchForm?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.handleSearch();
      });
      window.addEventListener("resize", this.onResize);
    }

    initializeMap() {
      const fallbackCenter = [
        Number.parseFloat(this.element.dataset.defaultLatitude) || 52.1326,
        Number.parseFloat(this.element.dataset.defaultLongitude) || 5.2913,
      ];
      const fallbackZoom =
        Number.parseInt(this.element.dataset.defaultZoom, 10) || 7;

      this.map = this.L.map(this.mapElement, {
        center: fallbackCenter,
        zoom: fallbackZoom,
        zoomControl: false,
        scrollWheelZoom: false,
      });

      this.L.control.zoom({ position: "bottomright" }).addTo(this.map);
      this.initializeTileLayer();

      this.mapElement.addEventListener("focusin", () =>
        this.map.scrollWheelZoom.enable(),
      );
      this.mapElement.addEventListener("focusout", () =>
        this.map.scrollWheelZoom.disable(),
      );
      window.requestAnimationFrame(() => this.map?.invalidateSize());
    }

    initializeTileLayer() {
      const apiKey = this.element.dataset.maptilerApiKey?.trim();
      const mapId = this.element.dataset.maptilerMapId?.trim() || "dataviz-v4";

      if (apiKey) {
        const tileUrl =
          `https://api.maptiler.com/maps/${encodeURIComponent(mapId)}` +
          `/{z}/{x}/{y}@2x.png?key=${encodeURIComponent(apiKey)}`;

        this.L.tileLayer(tileUrl, {
          attribution:
            '<a href="https://www.maptiler.com/copyright/" target="_blank" rel="noopener">&copy; MapTiler</a> ' +
            '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">&copy; OpenStreetMap contributors</a>',
          minZoom: 1,
          maxZoom: 19,
          tileSize: 512,
          zoomOffset: -1,
          crossOrigin: true,
        }).addTo(this.map);
        return;
      }

      this.mapElement.classList.add("looom-store-locator__map--osm");
      this.L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>',
        maxZoom: 19,
      }).addTo(this.map);
    }

    applyFilters() {
      if (!this.map) return;

      const selectedType =
        this.filterInputs.find((input) => input.checked)?.value || "all";
      const query = this.normalizeText(this.searchQuery);

      this.filteredStores = this.stores
        .filter(
          (store) => selectedType === "all" || store.storeType === selectedType,
        )
        .filter((store) => {
          if (!query || this.searchPosition) return true;
          return this.storeMatchesQuery(store, query);
        })
        .sort((first, second) => {
          if (first.distance === null || second.distance === null) return 0;
          return first.distance - second.distance;
        });

      if (
        !this.filteredStores.some((store) => store.id === this.activeStoreId)
      ) {
        this.activeStoreId = null;
      }

      this.renderResults();
      this.renderMarkers();
      this.fitMapToStores();

      const count = this.filteredStores.length;
      this.announce(
        this.translate(count === 1 ? "resultsCountOne" : "resultsCountOther", {
          count,
        }),
      );
    }

    async handleSearch() {
      const query = this.searchInput?.value.trim() || "";
      this.updateClearButton();
      this.geocodeController?.abort();
      this.geocodeController = null;
      this.setSearchLoading(false);
      this.searchQuery = query;
      this.clearSearchMarker();
      this.searchPosition = null;
      this.restoreDistanceOrigin();

      if (!query) {
        this.applyFilters();
        return;
      }

      const normalizedQuery = this.normalizeText(query);
      const selectedType =
        this.filterInputs.find((input) => input.checked)?.value || "all";
      const localMatches = this.stores
        .filter(
          (store) => selectedType === "all" || store.storeType === selectedType,
        )
        .filter((store) => this.storeMatchesQuery(store, normalizedQuery));

      if (localMatches.length) {
        this.applyFilters();
        return;
      }

      const apiKey = this.element.dataset.maptilerApiKey?.trim();
      if (!apiKey) {
        this.applyFilters();
        this.announce(this.translate("geocodingUnavailable"));
        return;
      }

      const controller = new AbortController();
      this.geocodeController = controller;
      this.setSearchLoading(true);

      try {
        const parameters = new URLSearchParams({
          key: apiKey,
          limit: "1",
          language: document.documentElement.lang.split("-")[0] || "nl",
          autocomplete: "false",
          types: "address,postal_code,place,locality,municipality",
        });
        const country = this.element.dataset.geocodingCountry?.trim();
        if (country) parameters.set("country", country);

        const response = await fetch(
          `https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json?${parameters}`,
          { signal: controller.signal },
        );
        if (!response.ok)
          throw new Error(`MapTiler geocoding returned ${response.status}.`);

        const data = await response.json();
        const feature = data.features?.find(
          (item) =>
            Array.isArray(item.center) &&
            Number.isFinite(item.center[0]) &&
            Number.isFinite(item.center[1]),
        );

        if (!feature) {
          this.searchPosition = null;
          this.restoreDistanceOrigin();
          this.applyFilters();
          this.announce(this.translate("locationNotFound"));
          return;
        }

        this.searchPosition = [feature.center[1], feature.center[0]];
        this.calculateStoreDistances(this.searchPosition);
        this.updateSearchMarker(feature.place_name || feature.text || query);
        this.applyFilters();
        this.announce(
          this.translate("sortedFrom", {
            location: feature.place_name || query,
          }),
        );
      } catch (error) {
        if (error.name === "AbortError") return;
        this.searchPosition = null;
        this.restoreDistanceOrigin();
        this.applyFilters();
        this.announce(this.translate("locationSearchError"));
        console.error("[LOOOM store locator] Geocoding failed.", error);
      } finally {
        if (this.geocodeController === controller) {
          this.geocodeController = null;
          this.setSearchLoading(false);
        }
      }
    }

    storeMatchesQuery(store, query) {
      return [store.name, store.city, store.postalCode].some((value) =>
        this.normalizeText(value).includes(query),
      );
    }

    setSearchLoading(loading) {
      if (!this.searchButton) return;
      this.searchButton.disabled = loading;
      this.searchForm?.setAttribute("aria-busy", String(loading));
    }

    updateClearButton() {
      if (!this.searchClearButton) return;
      this.searchClearButton.hidden = !this.searchInput?.value;
    }

    clearSearch() {
      this.geocodeController?.abort();
      this.geocodeController = null;
      this.setSearchLoading(false);
      if (this.searchInput) this.searchInput.value = "";
      this.searchQuery = "";
      this.searchPosition = null;
      this.clearSearchMarker();
      this.restoreDistanceOrigin();
      this.updateClearButton();
      this.applyFilters();
      this.searchInput?.focus();
      this.announce(this.translate("searchCleared"));
    }

    renderResults() {
      this.resultsElement.replaceChildren();

      if (!this.filteredStores.length) {
        this.showMessage(
          this.stores.length
            ? this.translate("noResults")
            : this.translate("noLocations"),
        );
        return;
      }

      const fragment = document.createDocumentFragment();
      this.filteredStores.forEach((store) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "looom-store-locator__result";
        button.dataset.storeId = store.id;
        button.setAttribute(
          "aria-current",
          store.id === this.activeStoreId ? "true" : "false",
        );
        button.setAttribute("aria-label", this.resultAriaLabel(store));

        const copy = document.createElement("span");
        copy.className = "looom-store-locator__result-copy";

        const name = document.createElement("span");
        name.className = "looom-store-locator__result-name";
        name.textContent = store.name;

        const address = document.createElement("span");
        address.className = "looom-store-locator__result-address";
        address.textContent = this.formatAddress(store);

        const distance = document.createElement("span");
        distance.className = "looom-store-locator__result-distance";
        distance.textContent =
          store.distance === null
            ? ""
            : `${this.formatDistance(store.distance)} km`;
        distance.hidden = store.distance === null;

        const arrow = document.createElement("span");
        arrow.className = "looom-store-locator__result-arrow";
        arrow.setAttribute("aria-hidden", "true");
        arrow.innerHTML =
          '<svg viewBox="0 0 16 16" fill="none"><path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        copy.append(name, address);
        button.append(copy, distance, arrow);
        button.addEventListener("click", () =>
          this.selectStore(store.id, { source: "list" }),
        );
        fragment.appendChild(button);
      });

      this.resultsElement.appendChild(fragment);
    }

    renderMarkers() {
      this.markers.forEach((marker) => marker.remove());
      this.markers.clear();

      this.filteredStores.forEach((store) => {
        const marker = this.L.marker([store.latitude, store.longitude], {
          icon: this.createMarkerIcon(store),
          keyboard: true,
          title: store.name,
          alt: store.name,
        });

        marker.bindPopup(this.popupMarkup(store), {
          className: "looom-store-locator__popup",
          closeButton: true,
          maxWidth: 260,
        });
        marker.on("click", () =>
          this.selectStore(store.id, { source: "marker" }),
        );
        marker.addTo(this.map);
        this.markers.set(store.id, marker);
      });
    }

    createMarkerIcon(store, active = false) {
      const classes = [
        "looom-store-locator__marker",
        `looom-store-locator__marker--${store.storeType}`,
      ];
      if (active) classes.push("looom-store-locator__marker--active");

      return this.L.divIcon({
        className: "looom-store-locator__marker-icon",
        html: `<span class="${classes.join(" ")}" aria-hidden="true"></span>`,
        iconSize: [23, 29],
        iconAnchor: [12, 29],
        popupAnchor: [0, -25],
      });
    }

    updateSearchMarker(label) {
      this.clearSearchMarker();
      if (!this.searchPosition) return;

      const icon = this.L.divIcon({
        className: "looom-store-locator__search-marker-icon",
        html: '<span class="looom-store-locator__search-marker" aria-hidden="true"></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        popupAnchor: [0, -8],
      });

      this.searchMarker = this.L.marker(this.searchPosition, {
        icon,
        keyboard: true,
        title: label,
        alt: `Gezochte locatie: ${label}`,
        zIndexOffset: 1000,
      })
        .bindPopup(
          `<p class="looom-store-locator__popup-address"><strong>Gezochte locatie</strong><br>${this.escapeHtml(label)}</p>`,
        )
        .addTo(this.map);
    }

    clearSearchMarker() {
      this.searchMarker?.remove();
      this.searchMarker = null;
    }

    selectStore(storeId, { source }) {
      const store = this.filteredStores.find((item) => item.id === storeId);
      const marker = this.markers.get(storeId);
      if (!store || !marker) return;

      this.activeStoreId = storeId;
      this.filteredStores.forEach((item) => {
        const itemMarker = this.markers.get(item.id);
        itemMarker?.setIcon(this.createMarkerIcon(item, item.id === storeId));
      });

      this.resultsElement
        .querySelectorAll(".looom-store-locator__result")
        .forEach((result) =>
          result.setAttribute(
            "aria-current",
            String(result.dataset.storeId === storeId),
          ),
        );

      this.map.setView(
        [store.latitude, store.longitude],
        Math.max(this.map.getZoom(), 14),
        {
          animate: true,
        },
      );
      marker.openPopup();

      if (source === "marker") {
        const result = this.resultsElement.querySelector(
          `.looom-store-locator__result[data-store-id="${CSS.escape(storeId)}"]`,
        );
        result?.scrollIntoView({
          behavior: this.reducedMotion() ? "auto" : "smooth",
          block: "nearest",
        });
      }

      this.announce(this.translate("selected", { name: store.name }));
    }

    fitMapToStores() {
      if (this.searchPosition) {
        if (!this.filteredStores.length) {
          this.map.setView(this.searchPosition, 13, { animate: false });
          return;
        }

        const nearbyStores = this.filteredStores.slice(0, 5);
        const bounds = this.L.latLngBounds([
          this.searchPosition,
          ...nearbyStores.map((store) => [store.latitude, store.longitude]),
        ]);
        this.map.fitBounds(bounds, {
          animate: false,
          padding: [42, 42],
          maxZoom: 14,
        });
        return;
      }

      if (!this.filteredStores.length) {
        this.map.closePopup();
        return;
      }

      if (this.filteredStores.length === 1) {
        const store = this.filteredStores[0];
        this.map.setView([store.latitude, store.longitude], 14, {
          animate: false,
        });
        return;
      }

      const bounds = this.L.latLngBounds(
        this.filteredStores.map((store) => [store.latitude, store.longitude]),
      );
      this.map.fitBounds(bounds, {
        animate: false,
        padding: [42, 42],
        maxZoom: 14,
      });
    }

    requestUserLocation() {
      if (!navigator.geolocation || !this.stores.length) return;

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          if (this.destroyed) return;
          this.userPosition = [coords.latitude, coords.longitude];
          if (!this.searchPosition) {
            this.calculateStoreDistances(this.userPosition);
            this.applyFilters();
          }
        },
        () => {
          this.userPosition = null;
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
      );
    }

    calculateStoreDistances(position) {
      this.stores.forEach((store) => {
        store.distance = this.distanceInKilometres(
          position[0],
          position[1],
          store.latitude,
          store.longitude,
        );
      });
    }

    restoreDistanceOrigin() {
      if (this.userPosition) {
        this.calculateStoreDistances(this.userPosition);
        return;
      }
      this.stores.forEach((store) => {
        store.distance = null;
      });
    }

    distanceInKilometres(latitudeA, longitudeA, latitudeB, longitudeB) {
      const earthRadius = 6371;
      const toRadians = (degrees) => (degrees * Math.PI) / 180;
      const latitudeDelta = toRadians(latitudeB - latitudeA);
      const longitudeDelta = toRadians(longitudeB - longitudeA);
      const a =
        Math.sin(latitudeDelta / 2) ** 2 +
        Math.cos(toRadians(latitudeA)) *
          Math.cos(toRadians(latitudeB)) *
          Math.sin(longitudeDelta / 2) ** 2;
      return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    formatDistance(distance) {
      if (distance < 10) return distance.toFixed(1);
      return Math.round(distance).toString();
    }

    translate(key, replacements = {}) {
      let message = this.translations[key] || "";
      Object.entries(replacements).forEach(([name, value]) => {
        message = message.replace(`{{ ${name} }}`, String(value));
      });
      return message;
    }

    formatAddress(store) {
      const locality = [store.postalCode, store.city].filter(Boolean).join(" ");
      return [store.address, locality, store.country]
        .filter(Boolean)
        .join(", ");
    }

    resultAriaLabel(store) {
      const parts = [store.name, this.formatAddress(store)];
      if (store.distance !== null) {
        parts.push(
          `${this.formatDistance(store.distance)} ${this.translate("distanceUnit")}`,
        );
      }
      return parts.filter(Boolean).join(", ");
    }

    popupMarkup(store) {
      const links = [];
      if (store.phone) {
        links.push(
          `<a href="tel:${this.escapeAttribute(store.phone.replace(/[^\d+]/g, ""))}">${this.escapeHtml(this.translate("call"))}</a>`,
        );
      }
      if (store.website) {
        const website = this.safeWebsiteUrl(store.website);
        if (website) {
          links.push(
            `<a href="${this.escapeAttribute(website)}" target="_blank" rel="noopener">${this.escapeHtml(this.translate("website"))}</a>`,
          );
        }
      }

      return [
        `<h3 class="looom-store-locator__popup-title">${this.escapeHtml(store.name)}</h3>`,
        `<p class="looom-store-locator__popup-address">${this.escapeHtml(this.formatAddress(store))}</p>`,
        links.length
          ? `<p class="looom-store-locator__popup-links">${links.join("")}</p>`
          : "",
      ].join("");
    }

    safeWebsiteUrl(value) {
      try {
        const candidate = /^[a-z][a-z\d+.-]*:/i.test(value)
          ? value
          : `https://${value}`;
        const url = new URL(candidate, window.location.origin);
        return ["http:", "https:"].includes(url.protocol) ? url.href : "";
      } catch (_error) {
        return "";
      }
    }

    escapeHtml(value) {
      const element = document.createElement("div");
      element.textContent = value;
      return element.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/"/g, "&quot;");
    }

    showMessage(message) {
      const element = document.createElement("p");
      element.className = "looom-store-locator__message";
      element.textContent = message;
      this.resultsElement.replaceChildren(element);
    }

    announce(message) {
      if (this.statusElement) this.statusElement.textContent = message;
    }

    reducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    debounce(callback, wait) {
      let timeout;
      return (...args) => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => callback(...args), wait);
      };
    }

    destroy() {
      this.destroyed = true;
      this.geocodeController?.abort();
      this.clearSearchMarker();
      window.removeEventListener("resize", this.onResize);
      this.map?.remove();
      this.element.dataset.initialized = "false";
    }
  }

  const instances = new WeakMap();

  const initialize = (scope = document) => {
    const sections = [
      ...(scope.matches?.("[data-looom-store-locator]") ? [scope] : []),
      ...scope.querySelectorAll("[data-looom-store-locator]"),
    ];

    sections.forEach((section) => {
      if (section.dataset.initialized === "true") return;
      section.dataset.initialized = "true";
      const instance = new LooomStoreLocator(section);
      instances.set(section, instance);
      instance.init();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), {
      once: true,
    });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    const section = event.target.querySelector("[data-looom-store-locator]");
    if (!section) return;
    instances.get(section)?.destroy();
    instances.delete(section);
  });
})();
